import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import ServiceRateHistory from "./ServiceRateHistory"; // NEW: Import rate history component

const EditServiceForm = ({ service, onUpdated, onCancel }) => {
  const [formData, setFormData] = useState({
    service_name: service.service_name || "",
    description: service.description || "",
    rate: service.rate || "",
    customer_id: service.customer_id || "",
    flat_rate: service.flat_rate || false,
    service_type: service.service_type || "service",
    mileage_record_not_required: service.mileage_record_not_required || false,
  });

  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const { tenant_id, user } = useContext(AuthContext);

  useEffect(() => {
    if (!tenant_id) return;

    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Failed to load customers", err));
  }, [tenant_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Updating service...");

    try {
      const payload = {
        ...formData,
        rate: parseFloat(formData.rate),
        tenant_id,
        updated_by: user?.user_id || "system",
      };

      await secureAxios.put(`/api/services/${service.service_id}`, payload);
      setMessage("Success! Service updated!");

      setTimeout(() => {
        setMessage("");
        if (onUpdated) onUpdated();
      }, 1000);
    } catch (err) {
      console.error("Error updating service", err);
      if (err.response?.status === 400) {
        setMessage(`Error: ${err.response.data.error || "Invalid data provided"}`);
      } else if (err.response?.status === 404) {
        setMessage("Error: Service not found");
      } else if (err.response?.status === 409) {
        setMessage(
          "Error: A service with this name already exists for this customer"
        );
      } else {
        setMessage("Error: Failed to update service.");
      }
    }
  };

  // Check if any changes were made
  const hasChanges = () => {
    return (
      formData.service_name !== service.service_name ||
      formData.description !== service.description ||
      parseFloat(formData.rate) !== parseFloat(service.rate) ||
      formData.customer_id !== service.customer_id ||
      formData.flat_rate !== service.flat_rate ||
      formData.service_type !== (service.service_type || "service") ||
      formData.mileage_record_not_required !==
        (service.mileage_record_not_required || false)
    );
  };

  return (
    <div>
      <h2>Edit Service</h2>
      {message && <p className="fw-bold">{message}</p>}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Service Name */}
        <div className="col-md-6">
          <label className="form-label">Service Name</label>
          <input
            className="form-control"
            name="service_name"
            value={formData.service_name}
            onChange={handleChange}
            required
            maxLength="100"
          />
        </div>

        {/* Rate */}
        <div className="col-md-6">
          <label className="form-label">
            Current Rate ($) {formData.flat_rate ? "(Total)" : "(Per Hour)"}
          </label>
          <input
            className="form-control"
            name="rate"
            type="number"
            step="0.001"
            min="0"
            max="9999.999"
            value={formData.rate}
            onChange={handleChange}
            required
          />
          {/* NEW: Help text pointing to rate history */}
          <small className="text-muted">
            To schedule future rate changes, use the Rate History section below.
          </small>
        </div>

        {/* Customer Dropdown */}
        <div className="col-12">
          <label className="form-label">Customer</label>
          <select
            className="form-select"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Service Type Dropdown */}
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            name="service_type"
            value={formData.service_type}
            onChange={(e) => {
              const newType = e.target.value;
              setFormData((prev) => ({
                ...prev,
                service_type: newType,
                // Reset mileage_record_not_required if switching to mileage type
                mileage_record_not_required:
                  newType === "mileage" ? false : prev.mileage_record_not_required,
              }));
            }}
            required
          >
            <option value="service">Service</option>
            <option value="mileage">Mileage</option>
          </select>
          <small className="text-muted">
            {formData.service_type === "service"
              ? "Hourly or flat-rate service work"
              : "Travel/mileage reimbursement"}
          </small>
        </div>

        {/* Description */}
        <div className="col-12">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            maxLength="500"
            placeholder="Describe the service..."
          ></textarea>
          <small className="text-muted">
            {formData.description.length}/500 characters
          </small>
        </div>

        {/* Flat Rate Checkbox */}
        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="flat_rate"
              id="flat_rate_edit"
              checked={formData.flat_rate}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="flat_rate_edit">
              <strong>Flat Rate Service</strong>
              <br />
              <small className="text-muted">
                Check this if the service is billed as a fixed amount regardless
                of hours worked
              </small>
            </label>
          </div>
        </div>

        {/* Mileage Record Not Required Checkbox - Only shown for 'service' type */}
        {formData.service_type === "service" && (
          <div className="col-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="mileage_record_not_required"
                id="mileage_record_not_required_edit"
                checked={formData.mileage_record_not_required}
                onChange={handleChange}
              />
              <label
                className="form-check-label"
                htmlFor="mileage_record_not_required_edit"
              >
                <strong>Mileage Record Not Required</strong>
                <br />
                <small className="text-muted">
                  Check this if job coaches do not need to log mileage when
                  providing this service (e.g., remote/virtual services)
                </small>
              </label>
            </div>
          </div>
        )}

        {/* Show what changed */}
        {hasChanges() && (
          <div className="col-12">
            <div className="alert alert-info">
              <small>You have unsaved changes</small>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!hasChanges()}
          >
            Update Service
          </button>
        </div>
      </form>

      {/* NEW: Rate History Section */}
      {service?.service_id && (
        <ServiceRateHistory
          serviceId={service.service_id}
          serviceName={service.service_name}
          currentRate={service.rate}
          onRateChange={(syncedRate) => {
            if (syncedRate !== undefined) {
              // Backend lazy-synced the rate; update the form field
              setFormData((prev) => ({ ...prev, rate: syncedRate }));
            }
          }}
        />
      )}
    </div>
  );
};

export default EditServiceForm;